import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, LogOut, Settings as SettingsIcon, Users, Plus, Crown } from 'lucide-react';
import { getCurrentUserData } from '../utils/auth';
import { getUserInitials, getFamilyInitials } from '../utils/userUtils';
import UserAvatar from './UserAvatar';

/**
 * NavBar Component
 * Reusable navigation bar for all category pages
 * 
 * @param {Object} props
 * @param {Array} props.families - List of families
 * @param {Object} props.selectedFamily - Currently selected family
 * @param {Function} props.setSelectedFamily - Function to set selected family
 * @param {Function} props.setShowPremiumModal - Function to show premium modal
 * @param {Function} props.handleLogout - Function to handle logout
 * @param {Object} props.currentUser - Current user object (optional, will load if not provided)
 * @param {String} props.className - Additional CSS classes for the nav element
 */
const NavBar = ({
    families = [],
    selectedFamily = null,
    setSelectedFamily = () => {},
    setShowPremiumModal = () => {},
    handleLogout = () => {},
    currentUser = null,
    className = ''
}) => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [userData, setUserData] = useState(currentUser);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Load user data if not provided
    useEffect(() => {
        if (!currentUser) {
            const user = getCurrentUserData();
            if (user) {
                setUserData(user);
            }
            
            // Refresh from API to ensure we have the latest data
            const refreshUserData = async () => {
                try {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                    const response = await fetch(`${apiUrl}/v1/auth/me`, {
                        credentials: 'include'
                    });
                    
                    if (response.ok) {
                        const freshUserData = await response.json();
                        localStorage.setItem('currentUser', JSON.stringify(freshUserData));
                        setUserData(freshUserData);
                    }
                } catch (error) {
                    console.error('Error refreshing user data:', error);
                }
            };
            
            refreshUserData();
        } else {
            setUserData(currentUser);
        }
    }, [currentUser]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id.toString());
        setShowFamilyDropdown(false);
    };

    return (
        <nav className={`bg-white shadow-sm ${className}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Left Section: Logo and Family Dropdown */}
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                        >
                            <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                            <span className="text-xl font-bold text-primary-blue">mifamli</span>
                        </button>

                        {/* Mi Famlis Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                }}
                            >
                                {selectedFamily ? (
                                    <>
                                        {selectedFamily.photo ? (
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                                                {getFamilyInitials(selectedFamily.name)}
                                            </div>
                                        )}
                                        <span>{selectedFamily.name}</span>
                                    </>
                                ) : (
                                    <span>Mi Famlis</span>
                                )}
                                <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Menu */}
                            {showFamilyDropdown && (
                                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                    {families.length > 0 ? (
                                        <>
                                            {families.map((family) => (
                                                <button
                                                    key={family.id}
                                                    onClick={() => handleFamilySelect(family)}
                                                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${
                                                        selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                    }`}
                                                >
                                                    {family.photo ? (
                                                        <img
                                                            src={family.photo}
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                                            {getFamilyInitials(family.name)}
                                                        </div>
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-800">{family.name}</p>
                                                        {family.tagline && (
                                                            <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                            <div className="border-t border-gray-200 my-2"></div>
                                        </>
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                            No families yet
                                        </div>
                                    )}
                                    <button
                                        onClick={() => {
                                            setShowFamilyDropdown(false);
                                            navigate('/famlis');
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                    >
                                        <Plus className="w-5 h-5" />
                                        <span>Add Family</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Section: Premium, Settings, and User Menu */}
                    <div className="flex items-center gap-4">
                        {/* Premium Button */}
                        <div className="relative">
                            <button
                                onClick={() => setShowPremiumModal(true)}
                                className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                    boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                }}
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    <span className="hidden sm:inline">Go Premium</span>
                                    <span className="sm:hidden">Premium</span>
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                            </button>
                            
                            {/* Golden Crown Overlay */}
                            <div className="absolute -top-1 -right-1 transform rotate-12">
                                <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                            </div>
                        </div>

                        {/* Family Settings Button */}
                        {selectedFamily && (
                            <button
                                onClick={() => navigate('/settings')}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Family Settings"
                            >
                                <SettingsIcon className="w-6 h-6 text-gray-600" />
                            </button>
                        )}

                        {/* User Avatar Menu */}
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                            >
                                {userData?.picture ? (
                                    <img 
                                        src={userData.picture} 
                                        alt={userData.name || 'User'} 
                                        className="w-10 h-10 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow border-2 border-white"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg shadow-md hover:shadow-lg transition-shadow border-2 border-white">
                                        {getUserInitials(userData?.name)}
                                    </div>
                                )}
                            </button>

                            {/* User Dropdown Menu */}
                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                    <div className="px-4 py-3 border-b border-gray-200">
                                        <p className="text-sm font-semibold text-gray-800">{userData?.name || 'User Account'}</p>
                                        <p className="text-xs text-gray-500 truncate">{userData?.email || 'Manage your profile'}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            // Profile functionality can be added here
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                    >
                                        <User className="w-5 h-5 text-gray-600" />
                                        <span>Profile</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            navigate('/settings');
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                    >
                                        <SettingsIcon className="w-5 h-5 text-gray-600" />
                                        <span>Family Settings</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            setShowPremiumModal(true);
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                    >
                                        <Crown className="w-5 h-5 text-yellow-500" />
                                        <span>Upgrade to Premium</span>
                                    </button>
                                    <div className="border-t border-gray-200 my-2"></div>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            handleLogout();
                                        }}
                                        className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600 font-semibold"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default NavBar;
